import React from 'react'
import { Typography } from '@mui/material'
import {
  Page,
  Section,
  Bullets,
  Bullet,
  Info,
  Warning,
  Screenshot,
} from '../../components/DocsComponents.jsx'
import DocLink from '../../components/DocLink.jsx'

const Overview = () => (
  <Page
    title="Finance Overview"
    intro="The Finance area brings everything money-related into one place: a single
    ledger of invoices and quotes across Event work and Patient Transport, plus payments,
    credit notes, unpaid-invoice chasing, and a CSV export for Xero."
  >
    <Section title="One ledger, two sources">
      <Typography>
        AmbuNet raises invoices and quotes in two places — Event Planning and
        Patient Transport — but the Finance area shows them together. Every
        invoice and quote carries a <strong>source</strong> (Event or PTS) and a
        link back to the event or PTS booking it belongs to, so the finance team
        can work from one list without hopping between modules.
      </Typography>
      <Bullets>
        <Bullet>
          <strong>Event invoices and quotes</strong> are created on the event
          record — see{' '}
          <DocLink
            url="/event-planning/shifts-and-documents"
            text="Event Shifts and Documents"
            docsPage
          />{' '}
          and{' '}
          <DocLink
            url="/event-planning/proposals-and-quotes"
            text="Proposals and Quotes"
            docsPage
          />
          .
        </Bullet>
        <Bullet>
          <strong>PTS invoices and quotes</strong> are priced from tariffs and
          managed inside the Patient Transport module — see{' '}
          <DocLink
            url="/patient-transport/invoicing"
            text="PTS Quoting, Invoicing & Billing"
            docsPage
          />
          .
        </Bullet>
      </Bullets>
    </Section>

    <Section title="Finding the Finance area">
      <Typography>
        The sidebar has a <strong>Finance</strong> section with three entries:
      </Typography>
      <Bullets>
        <Bullet>
          <DocLink url="/finance" text="Finance Centre" /> — a dashboard with
          headline figures and quick links.
        </Bullet>
        <Bullet>
          <DocLink url="/invoices" text="Invoices" /> — the combined Event and
          PTS invoice list.
        </Bullet>
        <Bullet>
          <DocLink url="/finance/quotes" text="Quotes" /> — the combined Event
          and PTS quote list.
        </Bullet>
      </Bullets>
      <Screenshot url="finance/sidebar-finance.png">
        The sidebar Finance section with Finance Centre, Invoices, and Quotes.
      </Screenshot>
    </Section>

    <Section title="The Finance Centre">
      <Typography>
        The Finance Centre gives an at-a-glance position and jumping-off points:
      </Typography>
      <Bullets>
        <Bullet>
          <strong>Outstanding</strong> — total unpaid balance across all
          invoices (click through to Invoices).
        </Bullet>
        <Bullet>
          <strong>Overdue</strong> — the unpaid balance on invoices flagged
          Overdue, or sent and past their due date (click through to Unpaid
          Invoices).
        </Bullet>
        <Bullet>
          <strong>Quotes Awaiting Response</strong> — quotes that have been sent
          but not yet accepted or declined.
        </Bullet>
        <Bullet>
          <strong>Total Quotes Tracked</strong> — every quote on record, from
          both Events and PTS.
        </Bullet>
      </Bullets>
      <Typography>
        Below the figures, quick links go to Invoices, Quotes, Unpaid Invoices,
        and the Stock Catalog (consumable unit costs used by make-ready).
      </Typography>
      <Info>
        The <strong>Outstanding</strong> figure is calculated across every
        invoice on record, including <strong>Draft</strong> invoices that have
        not yet been approved or sent — so it can run ahead of what has
        actually been billed. For a figure limited to issued invoices, use the
        tiles on the{' '}
        <DocLink
          url="/finance/invoices-and-credit-notes"
          text="Invoices"
          docsPage
        />{' '}
        page with the grid filtered to the statuses you care about.
      </Info>
      <Screenshot url="finance/finance-centre.png">
        Finance Centre with the four stat cards and the Go to quick links.
      </Screenshot>
    </Section>

    <Section title="Who can do this?">
      <Typography>
        Most Finance screens are controlled by the <strong>Finance</strong> team
        role on the employee record (set under Configuration → Employees →{' '}
        <strong>Manage Access</strong>). The server requires the Finance team
        for the invoice list, the quote list, recording payments, approving
        invoices, issuing credit notes, and the Xero export.
      </Typography>
      <Warning>
        The Finance entries in the sidebar are only shown to members of the
        Finance team (site administrators always see everything). Add the
        Finance team to anyone who actually works these screens.
      </Warning>
      <Info>
        Two related screens use different roles: opening an individual invoice
        requires the <strong>Event Planning</strong> team <em>or</em> PTS{' '}
        <strong>Co-Ordinator</strong> access, the Unpaid Invoices page requires
        the <strong>Event Planning</strong> team, and PTS billing screens
        require PTS <strong>Co-Ordinator</strong> access. Details are on each
        page in this section.
      </Info>
    </Section>
  </Page>
)

export default Overview
